import React, { FC, useEffect, useState } from 'react';
import './EditGraduateMarksModal.scss';

import TemplateModal from 'components/common/modals/TemplateModal';
import Button from 'components/common/ui-kit/Button';
import { InputV2 } from 'components/common/ui-kit/Input/view';
import useAppDispatch from 'hooks/common/useAppDispatch';
import useAppSelector from 'hooks/common/useAppSelector';
import { User } from 'models/enrollee';
import { EducationInformationDraft } from 'models/questionnaire/questionnaire';
import { UserRole } from 'models/user';
import { meanGrade } from 'pages/moderator/ModeratorEducationPage/view';
import { hideModal } from 'redux/actions';
import { enrolleesApi } from 'redux/api/questionnaire/enrollees-api';
import { useUpdateEducationResultsMutation } from 'redux/api/questionnaire/questionnaire-api';
import { selectUserRole } from 'redux/selectors';
import {
  selectCurrentQuestionnaire,
  selectCurrentApplication,
} from 'redux/selectors/staff/applications';
import { staffApplicationsSlice } from 'redux/slices/staff/staffApplicationsSlice';
import { supportQuestionnaireSlice } from 'redux/slices/staff/supportQuestionnaireSlice';

const EditGraduateMarksModal: FC = () => {
  const dispatch = useAppDispatch();
  const closeModal = () => dispatch(hideModal());

  const application = useAppSelector(selectCurrentApplication);
  const questionnaire = useAppSelector(selectCurrentQuestionnaire);
  const [updateStatistics, { isLoading }] = useUpdateEducationResultsMutation();

  const [triggerGetEnrollee] = enrolleesApi.useLazyGetByUserIdQuery();

  const educationProfileId = application?.educationProfileId;
  const userId = application?.userId;

  const educationInfo = questionnaire?.educationalInformationForm?.find(
    edu => edu.educationalInformationDraftId === educationProfileId
  );

  const [satisfactory, setSatisfactory] = useState(0);
  const [good, setGood] = useState(0);
  const [excellent, setExcellent] = useState(0);

  const role = useAppSelector(selectUserRole);
  const isSupport = role === UserRole.SUPPORT;

  useEffect(() => {
    if (educationInfo) {
      setSatisfactory(educationInfo.satisfactoryGrades ?? 0);
      setGood(educationInfo.goodGrades ?? 0);
      setExcellent(educationInfo.excellentGrades ?? 0);
    }
  }, [educationInfo]);

  const calculateMean = () => {
    const draft = {
      satisfactoryGrades: satisfactory,
      goodGrades: good,
      excellentGrades: excellent,
    };
    return meanGrade(draft as EducationInformationDraft);
  };

  const handleSave = async () => {
    if (!educationProfileId || !userId) {
      console.error('Missing required ids');
      return;
    }

    try {
      await updateStatistics({
        id: educationProfileId, // educationalInformationDraftId
        satisfactoryGrades: satisfactory,
        goodGrades: good,
        excellentGrades: excellent,
      }).unwrap();

      if (userId) {
        await triggerGetEnrollee(userId).unwrap();
      }
      const freshQuestionnaire = await triggerGetEnrollee(userId).unwrap();

      if (isSupport) {
        dispatch(
          supportQuestionnaireSlice.actions.setCurrentQuestionnaire(
            freshQuestionnaire
          )
        );
      } else {
        dispatch(
          staffApplicationsSlice.actions.setCurrentApplication({
            application: application!,
            questionnaire: freshQuestionnaire,
            user: application?.userId
              ? ({ id: application.userId } as User)
              : undefined,
          })
        );
      }
      closeModal();
    } catch (error) {
      console.error('error:', error);
    }
  };

  if (!application || !questionnaire) {
    return (
      <TemplateModal title="Редактировать средний балл">
        <div>Загрузка...</div>
      </TemplateModal>
    );
  }

  return (
    <TemplateModal title="Редактировать средний балл">
      <div className="graduate-marks-modal">
        <div className="graduate-group">
          <label>Количество оценок «Удовлетворительно»:</label>
          <InputV2
            type="number"
            min={0}
            value={satisfactory}
            onChange={e => setSatisfactory(Math.max(0, Number(e.target.value)))}
          />
          <label>Количество оценок «Хорошо»:</label>
          <InputV2
            type="number"
            min={0}
            value={good}
            onChange={e => setGood(Math.max(0, Number(e.target.value)))}
          />
          <label>Количество оценок «Отлично»:</label>
          <InputV2
            type="number"
            min={0}
            value={excellent}
            onChange={e => setExcellent(Math.max(0, Number(e.target.value)))}
          />
          <div style={{ marginTop: 16 }}>
            <strong>Средний балл: {calculateMean()}</strong>
          </div>
        </div>
        <div className="action">
          <Button theme="success" onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </div>
      </div>
    </TemplateModal>
  );
};

export default EditGraduateMarksModal;
